/**
 * SceneEditor: coordinator for the Scene Builder (Phase B+/C).
 *
 * Owns the editor subsystems — `EditorState`, `EditorPlacement`,
 * `EditorSelection`, `EditorGizmo`, `EditorHistory`, and (via `attachPanels`)
 * the property panel + object list — and wires them to the live
 * `SceneObjectManager` of the current scene.
 *
 * Lifecycle:
 *   F2 / toolbar  → toggle()            (enter/exit editing)
 *   asset button  → beginPlace(assetId) (placement preview follows crosshair)
 *   wheel         → onWheel             (tune free-depth)
 *   left-click    → place (if a prop is active) or pick-select
 *   W / E / R     → transform mode     (gizmo: move / rotate / scale)
 *   gizmo drag    → live transform      (syncs data + panel + selection bbox)
 *   Delete        → delete (history)    Ctrl+D → duplicate
 *   Ctrl+Z / Ctrl+Y → undo / redo
 *   X             → export()            (download scene.json with objects[])
 *
 * Event/portal panels, a real serializer, and a save service arrive in Phase D.
 */

import { Application, CULLFACE_NONE, Entity, Quat, Vec3 } from 'playcanvas';
import { resolveSceneSkyboxConfig, type SceneManager } from './SceneManager';
import type { CameraState } from '../player/types';
import { isMobileRuntime } from '../app/createApp';
import { eventBus } from '../events/EventBus';
import { EditorState, SPAWN_AREA_CONFIG_ID } from '../editor/EditorState';
import { EditorPlacement } from '../editor/EditorPlacement';
import { EditorSelection } from '../editor/EditorSelection';
import { EditorGizmo } from '../editor/EditorGizmo';
import { EditorHistory } from '../editor/EditorHistory';
import { EditorPropertyPanel } from '../editor/EditorPropertyPanel';
import type { PropertyMutators } from '../editor/EditorPropertyPanel';
import { EditorObjectList } from '../editor/EditorObjectList';
import { EditorAssetLibrary, createObjectFromAsset, mintObjectId } from '../editor/EditorAssetLibrary';
import type { EditorAsset } from '../editor/EditorAssetLibrary';
import { serializeScene } from '../editor/EditorSerializer';
import { EditorSaveService, type RemoteSceneSave } from '../editor/EditorSaveService';
import type { SceneObject } from './SceneObjectTypes';
import type { SceneSkyboxConfig } from '../config/manifest';
import { assignWorldLayers, makeEmissiveMaterial } from './markerFactory';

interface TransformSnapshot {
    pos: [number, number, number];
    rot: [number, number, number];
    scale: [number, number, number];
}

export class SceneEditor {
    private _app: Application;
    private _sm: SceneManager;
    private _camera: Entity;
    private _getScenes: () => string[];
    /** Registry 场景 id → 中文名（传送门列表行追加目标场景名用）。 */
    private _getSceneName: (id: string) => string | undefined;
    private _assetLib: EditorAssetLibrary;
    private _onHint: (text: string) => void;
    private _camState: CameraState;
    private _save: EditorSaveService;
    private _saveRemote: RemoteSceneSave | null;

    private _state: EditorState;
    private _placement: EditorPlacement;
    private _selection: EditorSelection;
    private _gizmo: EditorGizmo;
    private _history: EditorHistory;
    private _panel: EditorPropertyPanel | null = null;
    private _list: EditorObjectList | null = null;
    private _stateSub: () => void;

    private _enabled = false;

    /** Editor-only translucent rectangle showing the spawn area (F2). */
    private _spawnAreaVisual: Entity | null = null;
    /** The visible volume under _spawnAreaVisual (built lazily): one box mesh
     *  rendered exactly like collider markers — custom World layer (occluded by
     *  walls like any prop) + double-sided translucent material so it stays
     *  visible from inside the rect too. */
    private _spawnBox: Entity | null = null;
    /** Invisible gizmo target for the spawn area: the gizmo mutates this proxy
     *  (never the visible cage — scale must not compound on laid-out children);
     *  drag callbacks read it back into the manifest. */
    private _spawnProxy: Entity | null = null;
    private _spawnDragStart: { center: [number, number, number]; size: [number, number] } | null = null;

    /** In-progress gizmo drag's starting transform (captured on transform:start). */
    private _dragStart: TransformSnapshot | null = null;

    // Scratch (avoid per-frame allocation).
    private _q = new Quat();
    private _euler = new Vec3();

    /** Cursor position (CSS px) for cursor-driven aim/place/select. */
    private _mx = 0;
    private _my = 0;
    private _mouseValid = false;
    private _rayOrigin = new Vec3();
    private _rayDir = new Vec3();
    private _far = new Vec3();

    /** Notified with the placement asset id (or null) so the toolbar can sync. */
    onSelectChange: ((id: string | null) => void) | null = null;

    constructor(
        app: Application,
        sceneManager: SceneManager,
        camera: Entity,
        camState: CameraState,
        getScenes: () => string[],
        assetLib: EditorAssetLibrary,
        onHint: (text: string) => void,
        /** 远端保存回调（main.ts 注入：PUT /hub/scenes/:id/manifest，
         *  落库+写回静态 scene.json）；null = 无业务后端（保存直接失败）。 */
        saveRemote: RemoteSceneSave | null = null,
        /** 场景 id → 注册表中文名（对象列表传送门行显示「名称-目标场景」）。 */
        getSceneName: (id: string) => string | undefined = () => undefined
    ) {
        this._app = app;
        this._sm = sceneManager;
        this._camera = camera;
        this._getScenes = getScenes;
        this._getSceneName = getSceneName;
        this._assetLib = assetLib;
        this._onHint = onHint;
        this._camState = camState;
        this._save = new EditorSaveService();
        this._saveRemote = saveRemote;

        this._state = new EditorState(() => this._sm.current?.objects.objects ?? []);
        this._placement = new EditorPlacement(
            app,
            this._state,
            () => this._sm.current?.collision ?? null,
            (asset, pos) => this._onPlaced(asset, pos)
        );
        this._selection = new EditorSelection(
            app,
            camera,
            this._state,
            () => this._sm.current?.objects ?? null
        );
        this._gizmo = new EditorGizmo(app, camera, {
            onTransformStart: (id) => this._onGizmoStart(id),
            onTransformMove: (id) => this._onGizmoMove(id),
            onTransformEnd: (id) => this._onGizmoEnd(id)
        });
        this._history = new EditorHistory();

        // Re-attach the gizmo whenever the mode or selection changes.
        this._stateSub = this._state.subscribe(() => this._onStateChange());
    }

    get enabled(): boolean { return this._enabled; }
    get placing(): boolean { return this._placement.active; }
    get selectedId(): string | null { return this._state.selectedId; }
    get state(): EditorState { return this._state; }
    get assetLib(): EditorAssetLibrary { return this._assetLib; }
    /** True while a gizmo handle is being dragged (main.ts skips look/click). */
    get isGizmoDragging(): boolean { return this._gizmo.isDragging; }

    toggle(): void {
        this._enabled = !this._enabled;
        if (this._enabled) {
            this._state.setMode('select');
            // Re-entering with the 出生区 config item active: restore the
            // pseudo-selection so the gizmo re-attaches (W移动/R缩放).
            if (this._state.selectedAssetId === SPAWN_AREA_CONFIG_ID) {
                this._selection.select(null);
                this._state.select(SPAWN_AREA_CONFIG_ID);
            } else {
                this._state.select(null);
            }
            // The list/panel were built before the scene loaded, so their last
            // render is stale (empty). Force an emit so the object list picks up
            // the current scene's objects now that the editor is visible.
            this._state.notifyObjectsChanged();
            this._syncSpawnAreaVisual();
            this._onHint('编辑模式：左侧选配置项目查看对象 · 新增放置 · 点击选中 · W移动/E旋转/R缩放 · Del删除 · Ctrl+D复制 · Ctrl+Z/Y撤销重做 · 保存 · F2/Esc退出');
        } else {
            this._placement.cancel();
            this._gizmo.detach();
            this._selection.select(null);
            this._state.select(null);
            this.onSelectChange?.(null);
            this._syncSpawnAreaVisual();
            this._onHint('编辑模式已退出');
        }
    }

    /** Choose the active left-panel 配置项目 (cancels any in-progress placement
     *  first so the preview doesn't keep placing the previous type). Switching
     *  the item ALWAYS clears the object selection so the property panel drops
     *  the previous object's data. The 出生区 config item pseudo-selects the
     *  spawn area so the gizmo (W移动/R缩放) attaches; the in-scene rectangle
     *  only shows while the 出生区 item is active. */
    selectAsset(assetId: string): void {
        if (this._placement.active) this.cancel();
        this._state.setSelectedAsset(assetId);
        this._selection.select(null);
        if (assetId === SPAWN_AREA_CONFIG_ID) {
            this._state.select(SPAWN_AREA_CONFIG_ID);
        } else if (this._state.selectedId !== null) {
            this._state.select(null);
        }
        this._syncSpawnAreaVisual();
    }

    /** Build/update/hide the spawn-area volume. Shown only while the editor is
     *  enabled AND the 出生区 config item is the active one AND the manifest
     *  has a spawnArea (read-only-looking, but the gizmo drags it while the
     *  item stays selected). Rendered like a collider box (the reference the
     *  designers know): ONE translucent box mesh on the custom World layer via
     *  assignWorldLayers — that layer draws AFTER the Occlusion layer, so walls
     *  properly hide the volume (the old bar cage lived on the default layer
     *  with the gsplat, which never writes depth, so it shone through the
     *  entire scene and read as floating far away). Material is double-sided
     *  (cull NONE) so the volume reads from inside the rect too, where a plain
     *  box would be fully backface-culled. Vertical span: feet (center.y −
     *  1.55) to just above the eye (+0.55). */
    private _syncSpawnAreaVisual(): void {
        const active = this._enabled && this._state.selectedAssetId === SPAWN_AREA_CONFIG_ID;
        const area = active ? this._sm.current?.manifest.spawnArea : undefined;
        if (!area) {
            if (this._spawnAreaVisual) this._spawnAreaVisual.enabled = false;
            if (this._spawnProxy) this._spawnProxy.enabled = false;
            return;
        }
        if (!this._spawnAreaVisual) {
            const boxMat = makeEmissiveMaterial([0.35, 1, 0.55], 0.24);
            boxMat.cull = CULLFACE_NONE;
            boxMat.update();
            const root = new Entity('SpawnAreaRect');
            const box = new Entity('SpawnAreaBox');
            box.addComponent('render', { type: 'box' });
            box.render!.material = boxMat;
            assignWorldLayers(box);
            root.addChild(box);
            this._spawnBox = box;
            this._app.root.addChild(root);
            this._spawnAreaVisual = root;
            const proxy = new Entity('SpawnAreaProxy');
            this._app.root.addChild(proxy);
            this._spawnProxy = proxy;
        }
        const [w, d] = area.size;
        const FEET = -1.55,
            TOP = 0.55;
        this._spawnAreaVisual.enabled = true;
        this._spawnAreaVisual.setLocalPosition(area.center[0], area.center[1], area.center[2]);
        this._spawnBox!.setLocalScale(w, TOP - FEET, d);
        this._spawnBox!.setLocalPosition(0, (FEET + TOP) / 2, 0);
        // The gizmo proxy mirrors the center — but while a drag is live the
        // gizmo owns the proxy; don't fight it mid-drag.
        this._spawnProxy!.enabled = true;
        if (!this._spawnDragStart) {
            this._spawnProxy!.setLocalPosition(area.center[0], area.center[1], area.center[2]);
            this._spawnProxy!.setLocalScale(1, 1, 1);
        }
    }

    /** Track the mouse cursor (CSS px) for cursor-driven aim/place/select. */
    setMouse(x: number, y: number): void {
        this._mx = x;
        this._my = y;
        this._mouseValid = true;
    }

    /** Recompute the world-space cursor ray (origin + unit dir). Falls back to
     *  screen center before the first mousemove. */
    private _updateRay(): void {
        const mx = this._mouseValid ? this._mx : window.innerWidth / 2;
        const my = this._mouseValid ? this._my : window.innerHeight / 2;
        this._camera.getWorldTransform().getTranslation(this._rayOrigin);
        this._camera.camera!.screenToWorld(mx, my, 1000, this._far);
        this._rayDir.set(this._far.x - this._rayOrigin.x, this._far.y - this._rayOrigin.y, this._far.z - this._rayOrigin.z).normalize();
    }

    // ── Placement ────────────────────────────────────────────────────────
    beginPlace(assetId: string): void {
        if (!this._enabled) return;
        const asset = this._assetLib.get(assetId);
        if (!asset) return;
        this._placement.begin(asset);
        this._state.setMode('place');
        this._onHint(`放置「${asset.name}」：瞄准 · 滚轮调距 · 点击放置 · Esc 取消`);
        this.onSelectChange?.(assetId);
    }

    cancel(): void {
        this._placement.cancel();
        this._state.setMode('select');
        this.onSelectChange?.(null);
    }

    onWheel(deltaY: number): void { this._placement.onWheel(deltaY); }

    /** Left-click (down+up without dragging). Places if a placement is active,
     *  otherwise selects whatever is under the cursor. */
    onLeftClick(): void {
        if (!this._enabled) return;
        if (this._placement.active) { this._placement.commit(); return; }
        this._pickSelect();
    }

    private _pickSelect(): void {
        const mgr = this._sm.current?.objects;
        if (!mgr) return;
        this._updateRay();
        const id = mgr.pickRay(
            this._rayOrigin.x, this._rayOrigin.y, this._rayOrigin.z,
            this._rayDir.x, this._rayDir.y, this._rayDir.z
        );
        this._selection.select(id);
        this._state.select(id);
        if (id) {
            const o = mgr.get(id);
            this._onHint(`已选中「${o?.name ?? id}」· W移动/E旋转/R缩放 · Del删除 · Ctrl+D复制`);
        }
    }

    private _onPlaced(asset: EditorAsset, pos: [number, number, number]): void {
        const inst = this._sm.current;
        if (!inst) return;
        const existingIds = new Set(inst.objects.objects.map((o) => o.id));
        const obj: SceneObject = createObjectFromAsset(asset, pos, {
            sceneId: inst.manifest.id,
            scenes: this._getScenes()
        }, existingIds);
        this._cmdAdd(obj);
        this._selection.select(obj.id);
        this._state.select(obj.id);
        // Make the list show the fresh object even if it was placed outside
        // the currently active config item's filter.
        this._state.setSelectedAsset(asset.id);
        this._state.notifyObjectsChanged();
        this._onHint(`已放置「${obj.name}」· W移动/E旋转/R缩放`);
        this.onSelectChange?.(null);
        this._state.setMode('select');
    }

    // ── Gizmo / history ──────────────────────────────────────────────────

    /** Re-attach the gizmo to the selected marker for the current mode. Called
     *  on any state emit (mode change, selection change, objects changed). */
    private _onStateChange(): void {
        this._reattachGizmo();
    }

    /** Point the gizmo at the selected object's current marker entity. Needed
     *  after any marker rebuild (property panel edits call `refreshMarker`,
     *  which destroys + recreates the entity the gizmo is attached to).
     *  The 出生区 pseudo-selection (selectedId === SPAWN_AREA_CONFIG_ID) drives
     *  the invisible proxy instead — translate/scale only (the rect is
     *  axis-aligned, rotate is meaningless). */
    private _reattachGizmo(): void {
        const id = this._state.selectedId;
        const mgr = this._sm.current?.objects;
        let node = id ? mgr?.getMarkerEntity(id) : undefined;
        if (id === SPAWN_AREA_CONFIG_ID) {
            node = this._state.mode === 'rotate' || !this._sm.current?.manifest.spawnArea
                ? undefined
                : (this._spawnProxy ?? undefined);
        } else if (this._spawnDragStart) {
            // Mode switched / selection moved mid-drag (transform:end may never
            // fire) — consume the pending drag so the proxy resyncs cleanly.
            this._spawnDragStart = null;
            this._spawnProxy?.setLocalScale(1, 1, 1);
        }
        this._gizmo.setMode(this._state.mode);
        this._gizmo.attach(id, node ?? null);
    }

    private _onGizmoStart(id: string): void {
        if (id === SPAWN_AREA_CONFIG_ID) {
            const a = this._sm.current?.manifest.spawnArea;
            if (a) {
                this._spawnDragStart = {
                    center: [...a.center] as [number, number, number],
                    size: [...a.size] as [number, number]
                };
            }
            return;
        }
        const o = this._sm.current?.objects.get(id);
        if (!o) return;
        this._dragStart = {
            pos: [...o.transform.position] as [number, number, number],
            rot: [...o.transform.rotation] as [number, number, number],
            scale: [...o.transform.scale] as [number, number, number]
        };
    }

    /** Live drag: read the entity (the gizmo is moving it) → write back to the
     *  object data + selection bbox + property panel inputs. We must NOT rebuild
     *  the marker here (that would detach the gizmo mid-drag). */
    private _onGizmoMove(id: string): void {
        if (id === SPAWN_AREA_CONFIG_ID) {
            this._dragSpawnArea(false);
            return;
        }
        const mgr = this._sm.current?.objects;
        const o = mgr?.get(id);
        const m = mgr?.getMarkerEntity(id);
        if (!mgr || !o || !m) return;
        const snap = this._readEntityTransform(m);
        mgr.applyTransform(id, snap.pos, snap.rot, snap.scale);
        this._selection.follow(o);
        this._state.notifyTransformChanged();
    }

    private _onGizmoEnd(id: string): void {
        if (id === SPAWN_AREA_CONFIG_ID) {
            this._dragSpawnArea(true);
            return;
        }
        const mgr = this._sm.current?.objects;
        const o = mgr?.get(id);
        const m = mgr?.getMarkerEntity(id);
        const before = this._dragStart;
        this._dragStart = null;
        if (!mgr || !o || !m || !before) return;
        const after = this._readEntityTransform(m);
        mgr.applyTransform(id, after.pos, after.rot, after.scale);
        this._selection.follow(o);
        this._state.notifyTransformChanged();

        if (!this._snapshotEqual(before, after)) {
            this._history.push({
                label: 'transform',
                undo: () => this._applyTransform(id, before.pos, before.rot, before.scale),
                redo: () => this._applyTransform(id, after.pos, after.rot, after.scale)
            });
        }
    }

    /** Spawn-area gizmo drag: read the proxy (the gizmo moves/scales it) back
     *  into the manifest. Translate → new center (spawn follows); scale →
     *  start-size × proxy scale (clamped ≥0.5). At commit the proxy scale is
     *  consumed (reset to 1) and the change is pushed as a history command. */
    private _dragSpawnArea(commit: boolean): void {
        const m = this._sm.current?.manifest;
        if (!m || !m.spawnArea || !this._spawnProxy || !this._spawnDragStart) return;
        const before = this._spawnDragStart;
        const p = this._spawnProxy.getPosition();
        const s = this._spawnProxy.getLocalScale();
        const after = {
            center: [p.x, p.y, p.z] as [number, number, number],
            size: [
                Math.max(0.5, before.size[0] * Math.max(0.01, s.x)),
                Math.max(0.5, before.size[1] * Math.max(0.01, s.z))
            ] as [number, number]
        };
        m.spawnArea = { center: after.center, size: after.size };
        m.spawn = [...after.center] as [number, number, number];
        this._syncSpawnAreaVisual();
        this._state.notifyTransformChanged();
        if (!commit) return;
        this._spawnDragStart = null;
        this._spawnProxy.setLocalScale(1, 1, 1);
        const changed = before.center.some((v, i) => v !== after.center[i]) ||
            before.size.some((v, i) => v !== after.size[i]);
        if (changed) {
            this._history.push({
                label: 'spawnArea',
                undo: () => this._applySpawnArea(before.center, before.size),
                redo: () => this._applySpawnArea(after.center, after.size)
            });
        }
        this._state.spawnSettingsVersion++;
        this._state.notifyObjectsChanged();
        this._onHint('出生区已调整（记得点保存写入 scene.json）');
    }

    /** History undo/redo target for spawn-area transform drags. */
    private _applySpawnArea(center: [number, number, number], size: [number, number]): void {
        const m = this._sm.current?.manifest;
        if (!m) return;
        m.spawnArea = { center: [...center], size: [...size] };
        m.spawn = [...center] as [number, number, number];
        this._state.spawnSettingsVersion++;
        this._syncSpawnAreaVisual();
        this._state.notifyObjectsChanged();
    }

    /** Read an object's live transform from its marker entity (world pos/rot +
     *  local scale). Rotation is returned as [pitch, yaw, roll] degrees. */
    private _readEntityTransform(m: Entity): TransformSnapshot {
        const p = m.getPosition();
        const q = m.getRotation();
        q.getEulerAngles(this._euler);
        const s = m.getLocalScale();
        return {
            pos: [p.x, p.y, p.z],
            rot: [this._euler.x, this._euler.y, this._euler.z],
            scale: [s.x, s.y, s.z]
        };
    }

    /** Apply a transform from history (undo/redo) — updates data + entity +
     *  selection + panel, without rebuilding the marker. */
    private _applyTransform(id: string, pos: [number, number, number], rot: [number, number, number], scale: [number, number, number]): void {
        const mgr = this._sm.current?.objects;
        mgr?.applyTransform(id, pos, rot, scale);
        const o = mgr?.get(id);
        if (o) this._selection.follow(o);
        this._state.notifyTransformChanged();
    }

    /** Add an object as a history command (so it can be undone). */
    private _cmdAdd(obj: SceneObject): void {
        const inst = this._sm.current;
        if (!inst) return;
        inst.objects.add(obj);
        this._history.push({
            label: 'add',
            undo: () => {
                inst.objects.remove(obj.id);
                if (this._state.selectedId === obj.id) {
                    this._selection.select(null);
                    this._state.select(null);
                }
                this._state.notifyObjectsChanged();
            },
            redo: () => {
                inst.objects.add(obj);
                this._state.notifyObjectsChanged();
            }
        });
    }

    /** Delete an object as a history command (so it can be undone). */
    private _cmdDelete(id: string): void {
        const inst = this._sm.current;
        const o = inst?.objects.get(id);
        if (!inst || !o) return;
        inst.objects.remove(id);
        if (this._state.selectedId === id) {
            this._selection.select(null);
            this._state.select(null);
        }
        this._state.notifyObjectsChanged();
        this._history.push({
            label: 'delete',
            undo: () => {
                inst.objects.add(o);
                this._state.select(o.id);
                this._state.notifyObjectsChanged();
            },
            redo: () => {
                inst.objects.remove(id);
                if (this._state.selectedId === id) {
                    this._selection.select(null);
                    this._state.select(null);
                }
                this._state.notifyObjectsChanged();
            }
        });
    }

    /** Delete the currently selected object (keyboard Delete). History-tracked.
     *  The 出生区 pseudo-selection has no deletable object — ignore. */
    deleteSelected(): void {
        const id = this._state.selectedId;
        if (!id || id === SPAWN_AREA_CONFIG_ID) return;
        this._cmdDelete(id);
        this._onHint('已删除对象');
    }

    /** Duplicate the selected object (offset +1 x/z) as a history command. */
    duplicate(): void {
        if (!this._enabled) return;
        const id = this._state.selectedId;
        const mgr = this._sm.current?.objects;
        if (!id || !mgr) return;
        const o = mgr.get(id);
        if (!o) return;
        const clone: SceneObject = JSON.parse(JSON.stringify(o));
        const existingIds = new Set(mgr.objects.map((obj) => obj.id));
        clone.id = mintObjectId(o.type, existingIds);
        clone.name = `${o.name} 副本`;
        clone.transform.position = [
            o.transform.position[0] + 1,
            o.transform.position[1],
            o.transform.position[2] + 1
        ];
        this._cmdAdd(clone);
        this._selection.select(clone.id);
        this._state.select(clone.id);
        this._state.notifyObjectsChanged();
        this._onHint(`已复制「${clone.name}」`);
    }

    undo(): void {
        if (this._history.undo()) {
            this._state.notifyObjectsChanged();
            this._state.notifyTransformChanged();
            this._onHint('已撤销');
        }
    }

    redo(): void {
        if (this._history.redo()) {
            this._state.notifyObjectsChanged();
            this._state.notifyTransformChanged();
            this._onHint('已重做');
        }
    }

    private _snapshotEqual(a: TransformSnapshot, b: TransformSnapshot): boolean {
        const eq = (x: [number, number, number], y: [number, number, number]) =>
            Math.abs(x[0] - y[0]) < 1e-4 && Math.abs(x[1] - y[1]) < 1e-4 && Math.abs(x[2] - y[2]) < 1e-4;
        return eq(a.pos, b.pos) && eq(a.rot, b.rot) && eq(a.scale, b.scale);
    }

    // ── Per-frame ────────────────────────────────────────────────────────
    update(state: CameraState): void {
        if (!this._enabled) return;
        this._updateRay();
        this._placement.update(this._rayOrigin, this._rayDir);
        this._selection.update(state);
    }

    /** Set the current scene's spawn to the player's current pose and (re)center
     *  the spawn rectangle here (the 出生区 config page's create action). The
     *  area keeps its previous size (or a 4×4m default on first creation). */
    setSpawnFromCurrentPose(): void {
        const inst = this._sm.current;
        if (!inst) return;
        inst.manifest.spawn = [
            this._camState.position.x,
            this._camState.position.y,
            this._camState.position.z
        ];
        inst.manifest.spawnAngles = [
            this._camState.angles.x,
            this._camState.angles.y
        ];
        const prev = inst.manifest.spawnArea;
        inst.manifest.spawnArea = {
            center: [...inst.manifest.spawn] as [number, number, number],
            size: prev ? [...prev.size] as [number, number] : [4, 4]
        };
        // Stay pseudo-selected on the 出生区 config item: gizmo attaches (W移动/
        // R缩放) and the property panel re-renders from the unconfigured hint
        // to the editable form (spawnSettingsVersion bump forces it).
        this._selection.select(null);
        this._state.select(SPAWN_AREA_CONFIG_ID);
        this._state.setSelectedAsset(SPAWN_AREA_CONFIG_ID);
        this._state.spawnSettingsVersion++;
        this._syncSpawnAreaVisual();
        this._state.notifyObjectsChanged();
        this._onHint('已创建出生区（进场在矩形内随机出生 · W移动/R缩放可拖拽）· 调整后记得点保存');
    }

    /** Called by main.ts after a scene switch: clear selection + history + rebind. */
    sceneChanged(): void {
        this._placement.cancel();
        this._gizmo.detach();
        this._history.clear();
        this._selection.select(null);
        // Keep the 出生区 pseudo-selection in step with the active config item
        // across scene switches (the new scene's area loads fresh).
        if (this._state.selectedAssetId === SPAWN_AREA_CONFIG_ID) {
            this._state.select(SPAWN_AREA_CONFIG_ID);
        } else {
            this._state.select(null);
        }
        this._syncSpawnAreaVisual();
        this._state.notifyObjectsChanged();
    }

    // ── Panels (mounted by main.ts into the toolbar shell) ───────────────
    attachPanels(propertyMount: HTMLElement, listMount: HTMLElement): void {
        const mut: PropertyMutators = {
            setTransform: (id, pos, rot) => {
                const mgr = this._sm.current?.objects;
                mgr?.setTransform(id, pos, rot);
                const o = mgr?.get(id);
                if (o) this._selection.follow(o);
                this._state.notifyTransformChanged();
            },
            refresh: (id) => {
                this._sm.current?.objects.refreshMarker(id);
                this._selection.refresh();
                // The marker entity was rebuilt — re-point the gizmo at the new one.
                this._reattachGizmo();
            },
            mutate: (id, fn) => {
                const o = this._sm.current?.objects.get(id);
                if (o) fn(o);
            },
            changed: () => this._state.notifyObjectsChanged(),
            testEvent: (id, eventIndex) => {
                const o = this._sm.current?.objects.get(id);
                const ev = o?.interaction?.events[eventIndex];
                if (o && ev) {
                    // hideScene toggles for preview: restore if currently hidden.
                    if (ev.type === 'hideScene' && this._sm.contentHidden) {
                        eventBus.emit({ type: 'SCENE_VISIBILITY', visible: true, objectId: o.id });
                    } else {
                        eventBus.emit({ type: 'OBJECT_TRIGGER', objectId: o.id, name: o.name, actions: [ev] });
                    }
                }
            },
            testPortal: (id) => {
                const o = this._sm.current?.objects.get(id);
                if (o?.portal) {
                    eventBus.emit({
                        type: 'PORTAL_ENTER',
                        id: o.id,
                        name: o.name,
                        targetScene: o.portal.targetScene,
                        targetSpawn: o.portal.targetSpawn,
                        targetAngles: [o.portal.targetAngles[0], o.portal.targetAngles[1]]
                    });
                }
            },
            getScenes: () => this._getScenes(),
            getSceneSpawn: () => {
                const m = this._sm.current?.manifest;
                return m ? {
                    spawn: [...m.spawn] as [number, number, number],
                    spawnAngles: [...m.spawnAngles] as [number, number],
                    spawnArea: m.spawnArea
                        ? { center: [...m.spawnArea.center] as [number, number, number], size: [...m.spawnArea.size] as [number, number] }
                        : undefined
                } : null;
            },
            setSceneSpawn: (spawn, spawnAngles) => {
                const m = this._sm.current?.manifest;
                if (!m) return;
                m.spawn = [...spawn] as [number, number, number];
                m.spawnAngles = [...spawnAngles] as [number, number];
                // spawn doubles as the rectangle center — keep the area in sync
                // so the visual and the sampled range follow the panel edits.
                if (m.spawnArea) m.spawnArea.center = [...m.spawn] as [number, number, number];
                this._syncSpawnAreaVisual();
                this._state.notifyObjectsChanged();
            },
            setSceneSpawnArea: (size) => {
                const m = this._sm.current?.manifest;
                if (!m) return;
                const created = !m.spawnArea;
                m.spawnArea = { center: [...m.spawn] as [number, number, number], size: [...size] as [number, number] };
                if (created) this._state.spawnSettingsVersion++;
                this._syncSpawnAreaVisual();
                this._state.notifyObjectsChanged();
            },
            clearSceneSpawnArea: () => {
                const m = this._sm.current?.manifest;
                if (!m || !m.spawnArea) return;
                delete m.spawnArea;
                this._state.spawnSettingsVersion++;
                this._syncSpawnAreaVisual();
                this._state.notifyObjectsChanged();
            },
            setSpawnFromCurrentPose: () => this.setSpawnFromCurrentPose(),
            getSceneSkybox: () => {
                const skybox = this._sm.current?.manifest.skybox;
                return skybox ? { ...skybox } as SceneSkyboxConfig : null;
            },
            setSceneSkybox: (skybox) => {
                const inst = this._sm.current;
                if (!inst) return;
                if (skybox) inst.manifest.skybox = { ...skybox };
                else delete inst.manifest.skybox;
                void inst.skybox.load(resolveSceneSkyboxConfig(inst.manifest, isMobileRuntime()));
                this._state.notifyObjectsChanged();
            },
            getSceneMobileSkybox: () => {
                const skybox = this._sm.current?.manifest.mobileSkybox;
                return skybox ? { ...skybox } as SceneSkyboxConfig : null;
            },
            setSceneMobileSkybox: (skybox) => {
                const inst = this._sm.current;
                if (!inst) return;
                if (skybox) inst.manifest.mobileSkybox = { ...skybox };
                else delete inst.manifest.mobileSkybox;
                void inst.skybox.load(resolveSceneSkyboxConfig(inst.manifest, isMobileRuntime()));
                this._state.notifyObjectsChanged();
            }
        };
        this._panel = new EditorPropertyPanel(propertyMount, this._state, () => this._sm.current?.objects ?? null, mut);
        this._list = new EditorObjectList(listMount, this._state, () => this._sm.current?.objects ?? null, {
            select: (id) => {
                this._selection.select(id);
                this._state.select(id);
            },
            delete: (id) => this._cmdDelete(id),
            add: () => {
                const assetId = this._state.selectedAssetId;
                if (assetId) this.beginPlace(assetId);
            },
            createSpawnArea: () => this.setSpawnFromCurrentPose(),
            selectSpawnArea: () => this.selectAsset(SPAWN_AREA_CONFIG_ID)
        }, () => {
            const assetId = this._state.selectedAssetId;
            return assetId ? this._assetLib.get(assetId) ?? null : null;
        }, () => {
            const a = this._sm.current?.manifest.spawnArea;
            return a
                ? { center: [...a.center] as [number, number, number], size: [...a.size] as [number, number] }
                : null;
        }, () => {
            const m = this._sm.current?.manifest;
            if (!m?.skybox && !m?.mobileSkybox) return null;
            return {
                desktop: this._skyboxLabel(m.skybox),
                mobile: m.mobileSkybox ? this._skyboxLabel(m.mobileSkybox) : '跟随桌面'
            };
        }, (id) => this._getSceneName(id));
    }

    /** Short display label for a skybox config (cubemap folder / file stem). */
    private _skyboxLabel(cfg: SceneSkyboxConfig | undefined): string {
        if (!cfg) return '未配置';
        if (cfg.type === 'cubemap') {
            const m = cfg.faces.px.match(/\/assets\/sky\/(.+)\/[^/]+$/);
            return m?.[1] ?? 'cubemap';
        }
        return cfg.url.split('/').pop() || cfg.url;
    }

    // ── Serialize / save (Phase D) ───────────────────────────────────────

    /** Export the current scene as a downloaded `scene.json` (always download,
     *  for inspecting / sharing). Validates first and surfaces problems. */
    export(): void {
        const inst = this._sm.current;
        if (!inst) return;
        const { manifest, errors } = serializeScene(inst.manifest, [...inst.objects.objects]);
        if (errors.length) {
            console.warn('[export] 校验问题：\n' + errors.map((e) => '  - ' + e).join('\n'));
            this._onHint(`导出存在 ${errors.length} 个问题（见控制台），仍已下载`);
        }
        this._save.download(inst.manifest.id, manifest);
        this._onHint(`已导出 ${inst.manifest.id}.scene.json（对象 ${manifest.objects?.length ?? 0}）`);
    }

    /** Save the current scene via the injected remote handler (PUT to the
     *  server). Blocks on validation errors (returns without saving). Returns
     *  the user-facing result message (empty if there was no current scene) so
     *  the host can surface it prominently. */
    async save(): Promise<string> {
        const inst = this._sm.current;
        if (!inst) return '';
        const { manifest, errors } = serializeScene(inst.manifest, [...inst.objects.objects]);
        if (errors.length) {
            const msg = `保存失败：${errors.length} 个问题\n  - ${errors.slice(0, 4).join('\n  - ')}`;
            console.warn('[save] ' + msg);
            this._onHint(`保存失败：${errors.length} 个问题（见控制台）`);
            return `保存失败：${errors.length} 个问题（见控制台）`;
        }
        const result = await this._save.save(inst.manifest.id, manifest, this._saveRemote);
        this._onHint(result.message);
        return result.message;
    }

    destroy(): void {
        this._stateSub();
        this._placement.cancel();
        this._gizmo.destroy();
        this._selection.destroy();
        this._panel?.destroy();
        this._list?.destroy();
    }
}
